import { useState } from "react";
import { Badge, SectionLabel, cx } from "../StudioShared";
import {
  CheckCircle2, XCircle, Clock, MinusCircle, Play, ListOrdered, ScrollText, ShieldCheck, Loader2,
} from "lucide-react";

const STATUS_ICON = {
  success: <CheckCircle2 size={14} className="text-emerald-400" />,
  approved: <CheckCircle2 size={14} className="text-emerald-400" />,
  error: <XCircle size={14} className="text-red-400" />,
  rejected: <XCircle size={14} className="text-red-400" />,
  waiting: <Clock size={14} className="text-amber-400" />,
  changes_requested: <Clock size={14} className="text-amber-400" />,
  skipped: <MinusCircle size={14} className="text-slate-500" />,
};
const money = (c) => (c || c === 0 ? `$${Number(c).toFixed(4)}` : "—");

export default function RunReplayPanel({ run, busy, replaying, activeIndex, onPlayVisual, onResume }) {
  const [tab, setTab] = useState("timeline");
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(null);
  if (!run) return <div className="text-slate-500 text-sm p-4" data-testid="wf-run-empty">Run this workflow to see live execution and Mission Replay.</div>;

  const steps = run.steps || [];
  const totalTokens = steps.reduce((a, s) => a + (s.tokens || 0), 0);
  const totalCost = steps.reduce((a, s) => a + (s.cost || 0), 0);
  const totalMs = steps.reduce((a, s) => a + (s.exec_ms || 0), 0);
  const statusTone = { completed: "green", failed: "red", waiting: "amber", running: "gold" }[run.status] || "slate";

  return (
    <div className="space-y-4" data-testid="wf-replay">
      <div className="flex items-center justify-between">
        <Badge tone={statusTone} testid="wf-run-status">{run.status}</Badge>
        <div className="text-xs text-slate-500">{steps.length} steps · {totalTokens} tok · {money(totalCost)} · {totalMs}ms</div>
      </div>

      {run.status === "waiting" && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 space-y-2" data-testid="wf-approval-box">
          <div className="text-amber-300 text-sm font-medium flex items-center gap-2"><Clock size={14} /> Awaiting human action</div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optional comment…" data-testid="wf-approval-comment"
            className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none" />
          <div className="flex flex-wrap gap-2">
            <button data-testid="wf-approve-btn" disabled={busy} onClick={() => onResume("approve", comment)} className="bg-gold text-navy px-3 py-1.5 text-xs font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60">Approve</button>
            <button data-testid="wf-changes-btn" disabled={busy} onClick={() => onResume("request_changes", comment)} className="border border-amber-500/40 text-amber-300 px-3 py-1.5 text-xs rounded-sm hover:bg-amber-500/10 disabled:opacity-60">Request Changes</button>
            <button data-testid="wf-reject-btn" disabled={busy} onClick={() => onResume("reject", comment)} className="border border-red-500/40 text-red-300 px-3 py-1.5 text-xs rounded-sm hover:bg-red-500/10 disabled:opacity-60">Reject</button>
          </div>
        </div>
      )}

      <div className="flex gap-1 bg-navy border border-white/10 p-1 rounded-sm">
        {[["timeline", "Timeline", ListOrdered], ["visual", "Visual Replay", Play], ["audit", "Audit", ShieldCheck]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`wf-replay-tab-${k}`}
            className={cx("flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-sm transition-colors", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white")}>
            <Icon size={13} /> {l}
          </button>
        ))}
      </div>

      {tab === "timeline" && (
        <div className="space-y-1.5" data-testid="wf-timeline">
          {steps.map((s) => (
            <div key={s.index} className="flex items-center gap-2.5 border border-white/10 rounded-sm px-3 py-2">
              <span className="shrink-0">{STATUS_ICON[s.status] || STATUS_ICON.skipped}</span>
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm truncate">{s.label}</div>
                {s.note ? <div className="text-slate-500 text-[11px] truncate">{s.note}</div> : null}
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">{s.node_type}</span>
              {s.exec_ms ? <span className="text-[10px] text-slate-600 shrink-0 font-mono">{s.exec_ms}ms</span> : null}
            </div>
          ))}
        </div>
      )}

      {tab === "visual" && (
        <div className="space-y-3" data-testid="wf-visual">
          <button onClick={onPlayVisual} disabled={replaying} data-testid="wf-play-visual"
            className="w-full flex items-center justify-center gap-2 bg-gold text-navy px-4 py-2 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60">
            {replaying ? <><Loader2 size={15} className="animate-spin" /> Replaying…</> : <><Play size={15} /> Play visual replay on canvas</>}
          </button>
          <p className="text-xs text-slate-500">Watch each node light up on the canvas in execution order. Current: <span className="text-gold">{activeIndex >= 0 && steps[activeIndex] ? steps[activeIndex].label : "—"}</span></p>
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-2" data-testid="wf-audit">
          {steps.map((s) => (
            <div key={s.index} className="border border-white/10 rounded-sm">
              <button onClick={() => setOpen(open === s.index ? null : s.index)} data-testid={`wf-audit-step-${s.index}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5">
                <span className="shrink-0">{STATUS_ICON[s.status] || STATUS_ICON.skipped}</span>
                <span className="text-white text-sm truncate flex-1">{s.label}</span>
                {s.tokens ? <span className="text-[10px] font-mono text-gold shrink-0">{s.tokens}t</span> : null}
              </button>
              {open === s.index && (
                <div className="px-3 pb-3 space-y-2 text-xs">
                  {(s.provider || s.model) && (
                    <div className="flex flex-wrap gap-2">
                      {s.provider && <Badge tone="slate">{s.provider}</Badge>}
                      {s.model && <Badge tone="slate">{s.model}</Badge>}
                      {s.ai_mode && <Badge tone="green">{s.ai_mode === "live" ? "🟢 AI Drafted · Live" : s.ai_mode}</Badge>}
                      {s.tokens ? <Badge tone="gold">{s.tokens} tokens</Badge> : null}
                      {s.cost != null ? <Badge tone="gold">{money(s.cost)}</Badge> : null}
                      {s.exec_ms ? <Badge tone="slate">{s.exec_ms}ms</Badge> : null}
                    </div>
                  )}
                  {s.note ? <div className="text-slate-400">{s.note}</div> : null}
                  {s.input ? <div><SectionLabel className="text-gold mb-1">Prompt</SectionLabel><pre className="whitespace-pre-wrap text-slate-400 bg-navy border border-white/10 rounded-sm p-2 max-h-40 overflow-auto">{s.input}</pre></div> : null}
                  {s.output ? <div><SectionLabel className="text-gold mb-1">Output</SectionLabel><pre className="whitespace-pre-wrap text-slate-300 bg-navy border border-white/10 rounded-sm p-2 max-h-56 overflow-auto">{s.output}</pre></div> : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
